import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, ScrollView, Pressable, Platform, ActivityIndicator } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import useResponsiveness from '@/hooks/useResponsiveness';
import PublicFooter from '@/components/PublicFooter';
import FlowField from '@/components/Landing/FlowField';
import { blogApi } from '@/lib/blogApi';
import { blogApiSsr } from '@/lib/blogApiSsr';
import { BlogPostCard } from '@/components/Blog/BlogPostCard';
import { buildBlogCategoryStructuredData } from '@/lib/blogStructuredData';

const SITE_URL = 'https://gab.ai';
const _staticBlogCategoryCache = {};

function resolveSlug(params) {
  const raw = params?.slug;
  if (typeof raw === 'string') return raw.trim();
  if (Array.isArray(raw)) return String(raw[0] || '').trim();
  return '';
}

export async function generateStaticParams() {
  // Rendered on demand (client SPA), not prerendered. Returning [] keeps
  // `expo export` fast and avoids hammering the API at build time.
  return [];
}

const buildCategoryTitle = (cat) => {
  if (cat?.meta?.title) return cat.meta.title;
  if (cat?.name) return `${cat.name} — Gab AI Blog`;
  return 'Gab AI Blog';
};

const buildCategoryDescription = (cat) => {
  if (cat?.meta?.description) return cat.meta.description;
  if (cat?.description) return cat.description;
  if (cat?.name) {
    return `Read ${cat.name} articles, guides, and updates from the Gab AI team.`;
  }
  return 'Browse articles by topic on the Gab AI blog.';
};

const buildKeywords = (cat) => {
  if (cat?.meta?.keywords) return cat.meta.keywords;
  const base = ['Gab AI blog', 'Super Intelligence articles', 'Super Intelligence news', 'Super Intelligence tutorials'];
  if (cat?.name) base.unshift(cat.name, `${cat.name} articles`);
  return base.join(', ');
};

/** Flatten FAQ Q&A from `meta.pageCopy` blocks (`type: 'faq'`) for JSON-LD. */
function collectFaqItemsFromPageCopy(pageCopy) {
  if (!Array.isArray(pageCopy)) return [];
  const out = [];
  for (const block of pageCopy) {
    if (!block || block.type !== 'faq' || !Array.isArray(block.items)) continue;
    for (const item of block.items) {
      if (!item || typeof item !== 'object') continue;
      const question = String(item.question || item.title || '').trim();
      const answer = String(item.answer || item.description || item.copy || '').trim();
      if (question && answer) out.push({ question, answer });
    }
  }
  return out;
}

const buildFaqSchema = (faq) => {
  if (!Array.isArray(faq) || !faq.length) return null;
  const mainEntity = faq
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const name = String(item.question || item.title || '').trim();
      const text = String(item.answer || item.description || item.copy || '').trim();
      if (!name || !text) return null;
      return {
        '@type': 'Question',
        name,
        acceptedAnswer: { '@type': 'Answer', text },
      };
    })
    .filter(Boolean);
  if (!mainEntity.length) return null;
  return { '@type': 'FAQPage', mainEntity };
};

const buildStructuredData = (cat, posts, slug) => {
  const base = buildBlogCategoryStructuredData(cat, posts, slug);
  const faqSchema = buildFaqSchema(collectFaqItemsFromPageCopy(cat?.meta?.pageCopy));
  if (!faqSchema) return base;
  return {
    ...base,
    '@graph': [...base['@graph'], faqSchema],
  };
};

export default function BlogCategoryPage() {
  const params = useLocalSearchParams();
  const router = useRouter();
  const { colors, theme } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const isDark = isDarkTheme(theme);

  const slug = useMemo(() => resolveSlug(params), [params?.slug]);

  const [category, setCategory] = useState(null);
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, hasMore: false, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [loadMoreBusy, setLoadMoreBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setCategory(null);
      setPosts([]);
      setPagination({ page: 1, hasMore: false, total: 0, pages: 0 });
      return;
    }

    const cached = _staticBlogCategoryCache[slug];
    if (cached?.category) {
      setCategory(cached.category);
      setPosts(cached.posts || []);
      setPagination(cached.pagination || { page: 1, hasMore: false, total: 0, pages: 0 });
      setLoading(false);
    } else {
      setCategory(null);
      setPosts([]);
      setPagination({ page: 1, hasMore: false, total: 0, pages: 0 });
      setLoading(true);
    }
    setError(null);

    let cancelled = false;
    (async () => {
      try {
        const [cat, list] = await Promise.all([
          blogApi.getCategoryBySlug(slug),
          blogApi.listPosts({ page: 1, limit: 12, category: slug }),
        ]);
        if (cancelled) return;
        setCategory(cat);
        setPosts(list.posts || []);
        setPagination(list.pagination || { page: 1, hasMore: false, total: 0, pages: 0 });
      } catch (err) {
        if (!cancelled) {
          console.error('Blog category load error:', err);
          setError(err.message || 'Failed to load category');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleLoadMore = useCallback(async () => {
    if (!slug || loadMoreBusy || !pagination.hasMore) return;
    setLoadMoreBusy(true);
    try {
      const nextPage = (pagination.page || 1) + 1;
      const data = await blogApi.listPosts({ page: nextPage, limit: 12, category: slug });
      setPosts((prev) => [...prev, ...(data.posts || [])]);
      setPagination(data.pagination || pagination);
    } catch (err) {
      console.error('Blog category load more:', err);
    } finally {
      setLoadMoreBusy(false);
    }
  }, [slug, loadMoreBusy, pagination]);

  const handlePostPress = useCallback(
    (postSlug) => {
      router.push(`/blog/${postSlug}`);
    },
    [router],
  );

  const sectionPadding = isXS ? 20 : 40;
  const columns = isXS ? 1 : isSM ? 2 : 3;
  const gap = isXS ? 16 : 20;

  const meta = useMemo(() => {
    if (!slug) {
      return {
        title: 'Blog',
        description: 'Gab AI blog.',
        canonical: '/blog',
        keywords: 'Gab AI blog',
        structuredData: null,
        image: undefined,
        noindex: true,
      };
    }
    const cached = _staticBlogCategoryCache[slug];
    const cat = category || cached?.category;
    const list = (posts && posts.length ? posts : cached?.posts) || [];

    if (!cat && loading) {
      const label = slug
        .split('-')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      return {
        title: `${label || slug} — Gab AI Blog`,
        description: `Browse ${label || slug} articles, guides, and updates on the Gab AI blog.`,
        canonical: `/blog/category/${slug}`,
        keywords: ['Gab AI blog', label || slug, 'Super Intelligence articles'].filter(Boolean).join(', '),
        structuredData: null,
        image: undefined,
        noindex: false,
      };
    }

    if (!cat) {
      return {
        title: 'Category not found',
        description: 'This blog category could not be found.',
        canonical: `/blog/category/${slug}`,
        keywords: 'Gab AI blog',
        structuredData: null,
        image: undefined,
        noindex: true,
      };
    }
    return {
      title: buildCategoryTitle(cat),
      description: buildCategoryDescription(cat),
      canonical: `/blog/category/${slug}`,
      keywords: buildKeywords(cat),
      structuredData: buildStructuredData(cat, list, slug),
      image: cat.meta?.ogImage || undefined,
      noindex: false,
    };
  }, [slug, category, posts, loading]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (document.querySelector('style[data-blog-category-scroll]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-blog-category-scroll', 'true');
    style.textContent = `
      .blog-scroll::-webkit-scrollbar { width: 8px; }
      .blog-scroll::-webkit-scrollbar-track { background: transparent; }
      .blog-scroll::-webkit-scrollbar-thumb { background: rgba(150,150,150,0.2); border-radius: 4px; }
      .blog-scroll::-webkit-scrollbar-thumb:hover { background: rgba(150,150,150,0.4); }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.querySelector('style[data-blog-category-scroll]');
      if (el) document.head.removeChild(el);
    };
  }, []);

  const pageCopyBlocks = Array.isArray(category?.meta?.pageCopy) ? category.meta.pageCopy : [];

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title={meta.title}
        description={meta.description}
        canonical={meta.canonical}
        keywords={meta.keywords}
        structuredData={meta.structuredData}
        image={meta.image}
        noindex={meta.noindex}
        rssUrl="/json/blog/rss"
      />

      <ScrollView
        className="blog-scroll"
        accessibilityRole={safeAccessibilityRole('main')}
        nativeID="main-content"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center', paddingBottom: isXS ? 40 : 60 }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        <View
          style={{
            width: '100%',
            maxWidth: 1100,
            paddingHorizontal: sectionPadding,
            paddingTop: isXS ? 100 : 120,
          }}
        >
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <Pressable onPress={() => router.push('/')}>
              <Text size="s" color="tertiary">Home</Text>
            </Pressable>
            <Text size="s" color="tertiary">/</Text>
            <Pressable onPress={() => router.push('/blog')}>
              <Text size="s" color="tertiary">Blog</Text>
            </Pressable>
            {category?.name ? (
              <>
                <Text size="s" color="tertiary">/</Text>
                <Text size="s" weight="medium" color="secondary">
                  {category.name}
                </Text>
              </>
            ) : null}
          </View>

          {!loading && !category && !error && (
            <View style={{ alignItems: 'flex-start', paddingVertical: 24 }}>
              <Text weight="bold" style={{ fontSize: isXS ? 26 : 32, marginBottom: 12 }}>
                Category not found
              </Text>
              <Text size="m" color="secondary" style={{ marginBottom: 20 }}>
                There is no blog category at this address, or it may have been removed.
              </Text>
              <Button text="Back to Blog" variant="secondary" isRound onPress={() => router.push('/blog')} />
            </View>
          )}

          {error && !loading && (
            <View style={{ paddingVertical: 24 }}>
              <Text color="secondary">{error}</Text>
              <Button text="Try again" variant="secondary" isRound style={{ marginTop: 16 }} onPress={() => router.replace(`/blog/category/${slug}`)} />
            </View>
          )}

          {category && (
            <>
              <Text
                size="s"
                weight="medium"
                style={{
                  color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                  marginBottom: 12,
                }}
              >
                Blog
              </Text>
              <Text
                weight="bold"
                style={{
                  fontSize: isXS ? 32 : 44,
                  letterSpacing: -1,
                  marginBottom: 16,
                  lineHeight: isXS ? 38 : 48,
                }}
              >
                {category.name}
              </Text>
              {!!category.description && (
                <Text
                  size="l"
                  color="secondary"
                  style={{ lineHeight: 28, marginBottom: 24, maxWidth: 720 }}
                >
                  {category.description}
                </Text>
              )}

              {pageCopyBlocks.map((block, i) => {
                if (!block || typeof block !== 'object') return null;
                const title = String(block.title || '').trim();
                const copy = String(block.copy || block.subtitle || '').trim();
                if (!title && !copy) return null;
                return (
                  <View key={`pc-${i}`} style={{ marginBottom: 20, maxWidth: 720 }}>
                    {!!title && (
                      <Text weight="semibold" style={{ fontSize: 18, marginBottom: 8 }}>
                        {title}
                      </Text>
                    )}
                    {!!copy && (
                      <Text size="m" color="secondary" style={{ lineHeight: 26 }}>
                        {copy}
                      </Text>
                    )}
                  </View>
                );
              })}
            </>
          )}
        </View>

        <View
          style={{
            width: '100%',
            maxWidth: 1100,
            paddingHorizontal: sectionPadding,
            paddingTop: category ? 24 : 0,
          }}
        >
          {loading && (
            <View style={{ alignItems: 'center', paddingVertical: 48 }}>
              <ActivityIndicator size="large" color="#8B5CF6" />
              <Text size="s" color="tertiary" style={{ marginTop: 12 }}>
                Loading articles…
              </Text>
            </View>
          )}

          {!loading && category && posts.length === 0 && (
            <View
              style={{
                paddingVertical: 40,
                alignItems: 'center',
                borderRadius: 16,
                borderWidth: 1,
                borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
              }}
            >
              <Icon name="newspaper" size={28} color={isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'} />
              <Text size="m" color="secondary" style={{ marginTop: 12 }}>
                No published articles in this category yet.
              </Text>
            </View>
          )}

          {!loading && posts.length > 0 && (
            <>
              <Text weight="semibold" style={{ fontSize: isXS ? 18 : 20, marginBottom: 16 }}>
                Articles
                {typeof pagination.total === 'number' ? ` (${pagination.total})` : ''}
              </Text>
              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap,
                }}
              >
                {posts.map((post) => {
                  const gapDeduction = columns > 1 ? (gap * (columns - 1)) / columns : 0;
                  const columnPercent = 100 / columns;
                  return (
                    <View
                      key={post.id || post.slug}
                      style={{
                        width: columns === 1 ? '100%' : `calc(${columnPercent}% - ${gapDeduction}px)`,
                        flexGrow: 0,
                        flexShrink: 0,
                      }}
                    >
                      <BlogPostCard
                        post={post}
                        isDark={isDark}
                        isXS={isXS}
                        isSM={isSM}
                        onPress={handlePostPress}
                      />
                    </View>
                  );
                })}
              </View>

              {pagination.hasMore && (
                <View style={{ alignItems: 'center', marginTop: 28 }}>
                  <Button
                    text={loadMoreBusy ? 'Loading…' : 'Load more'}
                    variant="secondary"
                    isRound
                    disabled={loadMoreBusy}
                    onPress={handleLoadMore}
                  />
                </View>
              )}
            </>
          )}
        </View>

        <View style={{ width: '100%', maxWidth: 1100, marginTop: 48 }}>
          <PublicFooter />
        </View>
      </ScrollView>
    </View>
  );
}
